import React, { useEffect, useState, useRef, lazy, Component } from 'react';
import { Award, Languages, Heart, ChevronLeft, ChevronRight, MapPin, X, Check, MessageCircle } from 'lucide-react';
import { triggerCelebrationConfetti, triggerSubtleConfetti } from '../utils/confetti';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../src/contexts/LanguageContext';
import { trackEvent, EVENTS } from '../utils/posthog';
import { downloadCV } from '../utils/cvDownload';
import { createWhatsAppUrl } from '../utils/whatsapp';
const Hero = () => {
  const {
    t
  } = useTranslation();
  const {
    language
  } = useLanguage();
  // State for image gallery
  const [activeImage, setActiveImage] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [hoverPosition, setHoverPosition] = useState({
    x: 0,
    y: 0
  });
  const [showWorldsOverlay, setShowWorldsOverlay] = useState(false);
  // Images for the gallery (Singapore photos)
  const images = [{
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nKfHuS8HDdTfTK579ZQD2g/99761D6D-AA14-4409-A904-5EFF881597D5.jpg',
    location: 'Marina Bay, Singapore',
    story: 'Where my future in healthcare begins'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qSxqgfQ7idKZcAQB49WurF/AA51D6BF-8F78-4DC8-92BC-4BD9244F0C92.jpg',
    location: 'Downtown Singapore',
    story: 'Exploring my new professional home'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9tw7gWRQNjFoag9BGdUsax/Mona_-_Shot-6.jpg',
    location: 'Gardens by the Bay',
    story: 'Bridging cultures through healthcare'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/84iwNmAnW9n6H8F1zdjCbC/Mona_-_Shot-7.jpg',
    location: 'Marina Bay Waterfront',
    story: "Ready to contribute to Singapore's healthcare excellence"
  }];
  // Automatic image rotation with pause on hover
  useEffect(() => {
    if (isHovering) return;
    const interval = setInterval(() => {
      setActiveImage(current => (current + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isHovering, images.length]);
  const handleMouseMove = e => {
    const bounds = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - bounds.left) / bounds.width * 100;
    const y = (e.clientY - bounds.top) / bounds.height * 100;
    setHoverPosition({
      x,
      y
    });
  };
  const handleDownloadClick = e => {
    triggerSubtleConfetti(e.currentTarget);
    trackEvent(EVENTS.CV_DOWNLOAD_CLICKED, {
      language: language,
      source: 'hero_section'
    });
    try {
      downloadCV(language);
    } catch (error) {
      console.error('Failed to download CV:', error);
      alert('Unable to download CV. Please try again or contact mona.sudarat31@gmail.com');
    }
  };
  const handleWhatsAppClick = e => {
    triggerSubtleConfetti(e.currentTarget);
    trackEvent(EVENTS.WHATSAPP_CLICKED, {
      source: 'hero_section'
    });
    const message = `Hi Mona, I came across your profile and would like to discuss a job opportunity we have at:
- Company Name: [Your Company]
- Job Title: [Position Title]
- Compensation: [Salary Range]

Next Steps:
1. Please reply with your availability for a brief call
2. Share any questions you have about the role
3. We can schedule a formal interview

Looking forward to connecting!`;
    const whatsappUrl = createWhatsAppUrl('+358458426661', message);
    window.open(whatsappUrl, '_blank');
  };
  const handleWorldsToggle = () => {
    const newState = !showWorldsOverlay;
    setShowWorldsOverlay(newState);
    if (newState) {
      triggerSubtleConfetti(document.getElementById('worlds-button'));
      trackEvent(EVENTS.WORLDS_OVERLAY_OPENED, {
        source: 'hero_section'
      });
    } else {
      trackEvent(EVENTS.WORLDS_OVERLAY_CLOSED, {
        source: 'hero_section'
      });
    }
  };
  const getTitleFontClass = () => {
    if (language === 'ta') return 'text-3xl md:text-4xl lg:text-5xl';
    if (language === 'th' || language === 'zh') return 'text-3xl md:text-4xl lg:text-5xl';
    return 'text-4xl md:text-5xl lg:text-6xl';
  };
  return <section id="home" className="pt-[72px] min-h-screen bg-cream-white overflow-hidden" data-id="element-361">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full" data-id="element-362">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center py-section" data-id="element-363">
          {/* Left Column - Content */}
          <div className="lg:col-span-6 space-y-8 z-10" data-id="element-364">
            {/* Badge Component */}
            <div className="inline-flex items-center bg-sage-mint/10 border border-sage-mint rounded-full px-4 py-2 hover:bg-sage-mint/20 transition-all duration-300 cursor-default" data-id="element-365">
              <span className="text-xs text-sage-mint font-medium" data-id="element-366">
                {t('hero.badge')}
              </span>
            </div>

            {/* Main Heading */}
            <h1 className={`${getTitleFontClass()} font-light leading-tight text-charcoal`} data-id="element-367">
              {t('hero.title')}
            </h1>

            {/* Subheading */}
            <p className="text-base md:text-lg text-grey-600 leading-relaxed" data-id="element-368">
              Registered Nurse combining Finnish training rigor with Southeast
              Asian cultural intelligence. Specialized in geriatric care,
              patient safety, and emergency nursing with 20+ verified
              certifications.
            </p>

            {/* Stats Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-369">
              <div className="bg-white rounded-card shadow-card p-5 text-center hover:shadow-glow-coral hover:-translate-y-1 transition-all duration-300 cursor-default" onClick={e => triggerSubtleConfetti(e.currentTarget)} data-id="element-370">
                <span className="text-xl font-semibold text-coral-pink" data-id="element-371">
                  20+
                </span>
                <p className="text-sm text-grey-600" data-id="element-372">
                  {t('hero.stats.certifications')}
                </p>
              </div>
              <div className="bg-white rounded-card shadow-card p-5 text-center hover:shadow-glow-mint hover:-translate-y-1 transition-all duration-300 cursor-default" onClick={e => triggerSubtleConfetti(e.currentTarget)} data-id="element-373">
                <span className="text-xl font-semibold text-sage-mint" data-id="element-374">3</span>
                <p className="text-sm text-grey-600" data-id="element-375">
                  {t('hero.stats.languages')}
                </p>
              </div>
              <div className="bg-white rounded-card shadow-card p-5 text-center hover:shadow-glow-coral hover:-translate-y-1 transition-all duration-300 cursor-default" onClick={e => triggerSubtleConfetti(e.currentTarget)} data-id="element-376">
                <span className="text-xl font-semibold text-coral-pink" data-id="element-377">
                  90%
                </span>
                <p className="text-sm text-grey-600" data-id="element-378">
                  {t('hero.stats.geriatric')}
                </p>
              </div>
            </div>

            {/* CTA Group */}
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-379">
              <button id="download-cv-btn" className="bg-white text-sage-mint border border-sage-mint h-12 px-6 rounded-lg transition-all duration-300 hover:bg-sage-mint/5 hover:shadow-glow-mint hover:scale-105 active:scale-98" onClick={handleDownloadClick} data-id="element-380">
                {t('hero.cta.download')}
              </button>
              <button id="whatsapp-cta" className="bg-sage-mint text-white h-12 px-6 rounded-lg transition-all duration-300 hover:shadow-glow-mint hover:scale-105 active:scale-98 flex items-center justify-center gap-2" onClick={handleWhatsAppClick} data-id="element-381">
                <MessageCircle className="h-4 w-4" data-id="element-382" />
                <span data-id="element-383">WhatsApp Me</span>
              </button>
            </div>

            {/* Tertiary action */}
            <button className="text-sage-mint text-sm font-medium hover:text-sage-mint/70 transition-colors flex items-center gap-2" onClick={handleWorldsToggle} data-id="element-384">
              <span data-id="element-385">{t('hero.cta.explore')}</span>
              <ChevronRight className="h-4 w-4" data-id="element-386" />
            </button>
          </div>

          {/* Right Column - Image Gallery */}
          <div className="lg:col-span-6 relative" data-id="element-387">
            <div className="rounded-2xl overflow-hidden shadow-card relative group hover:shadow-glow-mint transition-all duration-500" onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)} onMouseMove={handleMouseMove} data-id="element-388">
              {/* Animated background gradient */}
              <div className="absolute -inset-1 bg-gradient-to-r from-sage-mint/30 via-coral-pink/20 to-sage-mint/30 rounded-2xl blur-md z-0" style={{
              animation: 'pulse 10s ease-in-out infinite',
              opacity: 0.5
            }} data-id="element-389" />
              {/* Image Gallery */}
              <div className="relative z-10 rounded-2xl overflow-hidden" data-id="element-390">
                <div className="relative" data-id="element-391">
                  <img src={images[activeImage].src} alt={`Registered nurse in ${images[activeImage].location} - ${images[activeImage].story}`} className="w-full h-[500px] object-cover transition-all duration-300" loading="lazy" width="800" height="500" data-id="element-392" />
                  {/* Simplified location and story overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-charcoal/70 to-transparent p-6" data-id="element-393">
                    <div className="flex items-center space-x-2 mb-2" data-id="element-394">
                      <MapPin className="h-4 w-4 text-white" data-id="element-395" />
                      <span className="text-sm font-medium text-white" data-id="element-396">
                        {images[activeImage].location}
                      </span>
                    </div>
                    <p className="text-white text-lg font-medium" data-id="element-397">
                      {images[activeImage].story}
                    </p>
                  </div>
                </div>
                {/* Navigation arrows - kept for basic slideshow functionality */}
                <button className="absolute top-1/2 left-4 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center shadow-card hover:bg-white hover:shadow-glow-mint z-20" onClick={() => setActiveImage(prev => (prev - 1 + images.length) % images.length)} data-id="element-398">
                  <ChevronLeft className="h-5 w-5 text-charcoal" data-id="element-399" />
                </button>
                <button className="absolute top-1/2 right-4 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center shadow-card hover:bg-white hover:shadow-glow-mint z-20" onClick={() => setActiveImage(prev => (prev + 1) % images.length)} data-id="element-400">
                  <ChevronRight className="h-5 w-5 text-charcoal" data-id="element-401" />
                </button>
                {/* Simplified image indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20" data-id="element-402">
                  {images.map((_, index) => <button key={index} className={`w-2 h-2 rounded-full transition-all duration-300 ${activeImage === index ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/80'}`} onClick={() => setActiveImage(index)} data-id="element-403" />)}
                </div>
              </div>
            </div>
            {/* Floating credential badges */}
            <div className="absolute top-4 right-0 bg-white rounded-lg shadow-card px-3 py-2 flex items-center space-x-2 transition-all duration-300 hover:shadow-glow-coral hover:scale-105 cursor-default" data-id="element-404">
              <Award className="h-4 w-4 text-coral-pink" data-id="element-405" />
              <span className="text-xs font-medium" data-id="element-406">Finnish RN</span>
            </div>
            <div className="absolute -bottom-4 right-20 bg-white rounded-lg shadow-card px-3 py-2 flex items-center space-x-2 transition-all duration-300 hover:shadow-glow-coral hover:scale-105 cursor-default z-20" data-id="element-407">
              <Award className="h-4 w-4 text-sage-mint" data-id="element-408" />
              <span className="text-xs font-medium" data-id="element-409">Medication License</span>
            </div>
            <div className="absolute -bottom-4 left-20 bg-white rounded-lg shadow-card px-3 py-2 flex items-center space-x-2 transition-all duration-300 hover:shadow-glow-mint hover:scale-105 cursor-default z-20" data-id="element-410">
              <Languages className="h-4 w-4 text-sage-mint" data-id="element-411" />
              <span className="text-xs font-medium" data-id="element-412">Trilingual</span>
            </div>
          </div>
        </div>
      </div>

      {/* Two Worlds Overlay - Appears when "Explore My Two Worlds" is clicked */}
      {showWorldsOverlay && <div className="fixed inset-0 bg-charcoal/80 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={handleWorldsToggle} data-id="element-413">
          <div className="bg-cream-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-lg" onClick={e => e.stopPropagation()} style={{
        animation: 'fadeIn 0.3s ease-in-out'
      }} data-id="element-414">
            <div className="p-6 border-b border-grey-400/20" data-id="element-415">
              <h2 className="text-2xl font-light text-charcoal" data-id="element-416">
                {t('worlds.title')}
              </h2>
              <button className="absolute top-6 right-6 w-8 h-8 rounded-full bg-grey-400/10 flex items-center justify-center hover:bg-grey-400/20" onClick={handleWorldsToggle} data-id="element-417">
                <span className="sr-only" data-id="element-418">Close</span>
                <X className="h-5 w-5 text-grey-600" data-id="element-419" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]" data-id="element-420">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-421">
                {/* Finland */}
                <div className="space-y-4" data-id="element-422">
                  <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden" data-id="element-423">
                    <img src="https://images.unsplash.com/photo-1476900543704-4312b78632f8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Finland" className="w-full h-full object-cover" data-id="element-424" />
                  </div>
                  <h3 className="text-xl font-medium text-charcoal" data-id="element-425">
                    {t('worlds.finland.title')}
                  </h3>
                  <ul className="space-y-2" data-id="element-426">
                    {t('worlds.finland.points', {
                  returnObjects: true
                }).map((point, index) => <li key={index} className="flex items-start" data-id="element-427">
                        <div className="w-5 h-5 rounded-full bg-sage-mint/20 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-428">
                          <div className="w-2 h-2 bg-sage-mint rounded-full" data-id="element-429"></div>
                        </div>
                        <span className="text-grey-600" data-id="element-430">{point}</span>
                      </li>)}
                  </ul>
                </div>
                {/* Southeast Asia */}
                <div className="space-y-4" data-id="element-431">
                  <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden" data-id="element-432">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9tw7gWRQNjFoag9BGdUsax/Mona_-_Shot-6.jpg" alt="Singapore" className="w-full h-full object-cover" data-id="element-433" />
                  </div>
                  <h3 className="text-xl font-medium text-charcoal" data-id="element-434">
                    {t('worlds.asia.title')}
                  </h3>
                  <ul className="space-y-2" data-id="element-435">
                    {t('worlds.asia.points', {
                  returnObjects: true
                }).map((point, index) => <li key={index} className="flex items-start" data-id="element-436">
                        <div className="w-5 h-5 rounded-full bg-coral-pink/20 flex items-center justify-center mt-0.5 mr-2 flex-shrink-0" data-id="element-437">
                          <div className="w-2 h-2 bg-coral-pink rounded-full" data-id="element-438"></div>
                        </div>
                        <span className="text-grey-600" data-id="element-439">{point}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
              {/* Bridging section */}
              <div className="mt-8 pt-8 border-t border-grey-400/20" data-id="element-440">
                <h3 className="text-xl font-medium text-charcoal text-center mb-6" data-id="element-441">
                  {t('worlds.bridge.title')}
                </h3>
                <div className="relative" data-id="element-442">
                  <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-sage-mint to-coral-pink" data-id="element-443"></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-444">
                    <div className="md:text-right" data-id="element-445">
                      <div className="inline-block bg-white rounded-lg shadow-card p-4 hover:shadow-glow-mint transition-all duration-300" data-id="element-446">
                        <h4 className="text-sage-mint font-medium mb-2" data-id="element-447">
                          {t('worlds.bridge.points.evidence.title')}
                        </h4>
                        <p className="text-grey-600 text-sm" data-id="element-448">
                          {t('worlds.bridge.points.evidence.desc')}
                        </p>
                      </div>
                    </div>
                    <div className="md:text-left md:pt-20" data-id="element-449">
                      <div className="inline-block bg-white rounded-lg shadow-card p-4 hover:shadow-glow-coral transition-all duration-300" data-id="element-450">
                        <h4 className="text-coral-pink font-medium mb-2" data-id="element-451">
                          {t('worlds.bridge.points.cultural.title')}
                        </h4>
                        <p className="text-grey-600 text-sm" data-id="element-452">
                          {t('worlds.bridge.points.cultural.desc')}
                        </p>
                      </div>
                    </div>
                    <div className="md:text-right md:pt-20" data-id="element-453">
                      <div className="inline-block bg-white rounded-lg shadow-card p-4 hover:shadow-glow-mint transition-all duration-300" data-id="element-454">
                        <h4 className="text-sage-mint font-medium mb-2" data-id="element-455">
                          {t('worlds.bridge.points.tech.title')}
                        </h4>
                        <p className="text-grey-600 text-sm" data-id="element-456">
                          {t('worlds.bridge.points.tech.desc')}
                        </p>
                      </div>
                    </div>
                    <div className="md:text-left md:pt-40" data-id="element-457">
                      <div className="inline-block bg-white rounded-lg shadow-card p-4 hover:shadow-glow-coral transition-all duration-300" data-id="element-458">
                        <h4 className="text-coral-pink font-medium mb-2" data-id="element-459">
                          {t('worlds.bridge.points.family.title')}
                        </h4>
                        <p className="text-grey-600 text-sm" data-id="element-460">
                          {t('worlds.bridge.points.family.desc')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-sage-mint/5 rounded-bl-full -z-10" data-id="element-461"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-coral-pink/5 rounded-tr-full -z-10" data-id="element-462"></div>
    </section>;
};
export default Hero;